
import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import Layout from "@/components/Layout";
import LoadingPage from "@/components/LoadingPage";
import { useIsMobile } from "@/hooks/use-mobile";

// Eagerly load critical components
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";

// Lazy load non-critical page components
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Configuration = lazy(() => import("./pages/Configuration"));
const About = lazy(() => import("./pages/About"));
const PopulismScore = lazy(() => import("./pages/PopulismScore"));
const CounterStrategies = lazy(() => import("./pages/CounterStrategies"));
const PopulistScan = lazy(() => import("./pages/PopulistScan"));
const PopulismImpact = lazy(() => import("./pages/PopulismImpact"));
const PopulismHarmful = lazy(() => import("./pages/PopulismHarmful"));
const PoliticalStand = lazy(() => import("./pages/PoliticalStand"));
const WhyPif = lazy(() => import("./pages/WhyPif"));
const PifInsightTopics = lazy(() => import("./pages/PifInsightTopics"));
const PifInsightDetail = lazy(() => import("./pages/PifInsightDetail"));
const ContentFeed = lazy(() => import("./pages/ContentFeed"));
const ContentDetail = lazy(() => import("./pages/ContentDetail"));
const AdminContentFeed = lazy(() => import("./pages/AdminContentFeed"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const MobileHome = lazy(() => import("./pages/MobileHome"));
const Profile = lazy(() => import("./pages/Profile"));

// Conditional Home Page component based on device type
const HomePage = () => {
  const isMobile = useIsMobile();
  
  if (isMobile) {
    return <MobileHome />;
  }
  return <ContentFeed />;
};

// Redirect component for explorer with ID
const ExplorerToDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/pif-insight/${id}`} replace />;
};

const queryClient = new QueryClient();

const App = () => {
  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={<LoadingPage />}>
                <Routes>
                  <Route element={<Layout />}>
                    {/* Use conditional HomePage component for the main route */}
                    <Route path="/" element={<HomePage />} />
                    
                    {/* Move old home/analyze to a protected route */}
                    <Route 
                      path="/analyze" 
                      element={
                        <ProtectedRoute>
                          <Index />
                        </ProtectedRoute>
                      } 
                    />
                    
                    <Route path="/about" element={<About />} />
                    <Route path="/why-pif" element={<WhyPif />} />
                    <Route path="/populism-score" element={<PopulismScore />} />
                    <Route path="/counter-strategies" element={<CounterStrategies />} />
                    <Route path="/populist-scan" element={<PopulistScan />} />
                    
                    {/* User profile page */}
                    <Route 
                      path="/profile" 
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Redirect old content feed path to root */}
                    <Route path="/content-feed" element={<Navigate to="/" replace />} />
                    <Route path="/content-feed/:id" element={<ContentDetail />} />
                    
                    {/* Redirect from old PIF Insight to Topics */}
                    <Route path="/pif-insight" element={<Navigate to="/pif-insight/topics" replace />} />
                    
                    {/* Make PIF Insight Topics protected */}
                    <Route 
                      path="/pif-insight/topics" 
                      element={
                        <ProtectedRoute>
                          <PifInsightTopics />
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/pif-insight/:id" element={<PifInsightDetail />} />
                    
                    {/* Redirect from old explorer URLs to PIF Insight */}
                    <Route path="/pif-insights-explorer" element={<Navigate to="/pif-insight/topics" replace />} />
                    <Route path="/pif-insights-explorer/:id" element={<ExplorerToDetailRedirect />} />
                    <Route path="/populism-impact" element={<PopulismImpact />} />
                    <Route path="/populism-harmful" element={<PopulismHarmful />} />
                    <Route path="/political-stand" element={<PoliticalStand />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route 
                      path="/dashboard" 
                      element={
                        <AdminRoute>
                          <Dashboard />
                        </AdminRoute>
                      } 
                    />
                    <Route 
                      path="/dashboard/config" 
                      element={
                        <AdminRoute>
                          <Configuration />
                        </AdminRoute>
                      } 
                    />
                    <Route 
                      path="/admin/content-feed" 
                      element={
                        <AdminRoute>
                          <AdminContentFeed />
                        </AdminRoute>
                      } 
                    />
                    <Route 
                      path="/admin" 
                      element={
                        <AdminRoute>
                          <AdminDashboard />
                        </AdminRoute>
                      } 
                    />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
              </Suspense>
            </TooltipProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>
  );
};

export default App;
